import { RDSubscription } from "../../../../../../models";
import {
    CustomerNewSubscriptionIcim,
    CustomerNewSubscriptionIcims,
    SubscriptionGroup,
} from "../../../../../../models/state";
import { BroadbandSubTab, CategoryTab, MobileSubTab } from "./types";
import {
    isZeroPlaySubscription,
    isBroadbandCode,
    isPrepaidSubscription,
} from "../../../../../../shared/subscriptionCodeUtils";
import { MAX_AGE_BARN } from "../../../../../../constants/app";

// Ordered by priority: the first prefix with an available subscription is preselected.
const UNLIMITED_PREFERRED_OFFERING_CODES: string[] = ["TELIA_X_KAMPANJE_2026_2", "TELIA_X_BASIC"];

export const isBroadbandSubscription = (offeringCode?: string): boolean => isBroadbandCode(offeringCode as never);

export type ExistingForChangeDisplay = { code: string; name: string };

export const getSubscriptionOptions = (
    subscriptions: RDSubscription[],
    existingForChangeDisplay: ExistingForChangeDisplay | undefined,
): string[] => [
    ...(existingForChangeDisplay &&
    !subscriptions.some((s) => `${s.offeringCode}.${s.icim}` === existingForChangeDisplay.code)
        ? [existingForChangeDisplay.code]
        : []),
    ...subscriptions.map((sub) => `${sub.offeringCode}.${sub.icim}`),
];

export const getFirstOfferingCode = (
    groups: Pick<CustomerNewSubscriptionIcim, "group" | "subscriptions">[],
    groupName = "regular",
) => {
    const group = groups.find((g) => g.group === groupName) ?? groups[0];
    const sub = group?.subscriptions[0];
    return sub ? `${sub.offeringCode}.${sub.icim}` : undefined;
};

export const getFirstSubscriptionForMobileSubTab = (
    subs: RDSubscription[],
    mobileSubTab: MobileSubTab,
): RDSubscription | undefined => {
    if (mobileSubTab === "UNLIMITED") {
        for (const preferredCode of UNLIMITED_PREFERRED_OFFERING_CODES) {
            const preferredSub = subs.find((s) => s.offeringCode.startsWith(preferredCode));
            if (preferredSub) return preferredSub;
        }
        return subs.find((s) => s.dataUnlimited) ?? subs[0];
    }

    if (mobileSubTab === "LIMITED") {
        return subs.find((s) => !s.dataUnlimited && !(s.maxAgeAllowed && s.maxAgeAllowed <= MAX_AGE_BARN)) ?? subs[0];
    }

    if (mobileSubTab === "CHILD") {
        return subs.find((s) => s.maxAgeAllowed && s.maxAgeAllowed <= MAX_AGE_BARN) ?? subs[0];
    }

    return subs[0];
};

export const getInitialSubscriptionGroup = (
    tabState: { category: CategoryTab; mobileSubTab: MobileSubTab },
    icims: CustomerNewSubscriptionIcims,
    preferredGroup: SubscriptionGroup,
): SubscriptionGroup => {
    let groups = icims.mhs;
    if (tabState.category === "BROADBAND") groups = icims.mbb;
    else if (tabState.category === "TJENESTER") groups = icims.vas;
    else if (tabState.category === "TV") groups = [];
    else if (tabState.mobileSubTab === "PREPAID") groups = icims.prepaid;

    const availableGroups = groups.filter((g) => g.subscriptions.length > 0);
    const group =
        availableGroups.find((g) => g.group === preferredGroup) ??
        availableGroups.find((g) => g.group === "regular") ??
        availableGroups[0];
    return group?.group ?? "regular";
};

export const getInitialOfferingCode = (
    currentOfferingCode: string | null | undefined,
    tabState: { category: CategoryTab; mobileSubTab: MobileSubTab },
    icims: CustomerNewSubscriptionIcims,
    preferredGroup = "regular",
): string | undefined => {
    if (currentOfferingCode) return currentOfferingCode;
    const { category, mobileSubTab } = tabState;
    if (category === "BROADBAND") {
        return getFirstOfferingCode(icims.mbb, preferredGroup);
    }
    if (category === "TJENESTER") {
        return getFirstOfferingCode(icims.vas, preferredGroup);
    }
    if (mobileSubTab === "PREPAID") {
        return getFirstOfferingCode(icims.prepaid, preferredGroup);
    }
    const mhsGroup = icims.mhs.find((g) => g.group === preferredGroup) ?? icims.mhs[0];
    const subs = mhsGroup?.subscriptions ?? [];
    const firstSub = getFirstSubscriptionForMobileSubTab(subs, mobileSubTab);
    return firstSub ? `${firstSub.offeringCode}.${firstSub.icim}` : undefined;
};

export const getTabStateForOfferingCode = (offeringCode: string | undefined, subscriptions: RDSubscription[]) => {
    const defaultState = {
        category: "MOBILE" as CategoryTab,
        mobileSubTab: "UNLIMITED" as MobileSubTab,
        broadbandSubTab: "MBB_MOBIL" as BroadbandSubTab,
    };
    if (!offeringCode) return defaultState;
    const sub = subscriptions.find((s) => `${s.offeringCode}.${s.icim}` === offeringCode);
    if (!sub) return defaultState;
    if (isZeroPlaySubscription(sub.offeringCode)) {
        return { ...defaultState, category: "TJENESTER" as CategoryTab };
    }
    if (isBroadbandSubscription(sub.offeringCode)) {
        return {
            ...defaultState,
            category: "BROADBAND" as CategoryTab,
            broadbandSubTab: "MBB_MOBIL" as BroadbandSubTab,
        };
    }
    if (isPrepaidSubscription(sub.offeringCode)) {
        return { ...defaultState, mobileSubTab: "PREPAID" as MobileSubTab };
    }
    if (sub.dataUnlimited) return defaultState;
    if (sub.maxAgeAllowed && sub.maxAgeAllowed <= MAX_AGE_BARN) {
        return { ...defaultState, mobileSubTab: "CHILD" as MobileSubTab };
    }
    return { ...defaultState, mobileSubTab: "LIMITED" as MobileSubTab };
};

export type CategoryOfferingCodeResult = {
    offeringCode: string | undefined;
    resetMobileTab?: MobileSubTab;
    resetBroadbandTab?: BroadbandSubTab;
};

export const getInitialOfferingCodeForCategory = (
    cat: CategoryTab,
    icims: CustomerNewSubscriptionIcims,
    activeMobileTab: MobileSubTab,
    activeBroadbandTab: BroadbandSubTab,
    preferredGroup = "regular",
): CategoryOfferingCodeResult => {
    if (cat === "MOBILE") {
        const isPrepaid = activeMobileTab === "PREPAID";
        const regularGroup = isPrepaid
            ? (icims.prepaid.find((g) => g.group === preferredGroup) ?? icims.prepaid[0])
            : (icims.mhs.find((g) => g.group === preferredGroup) ?? icims.mhs[0]);
        const subs = regularGroup?.subscriptions ?? [];
        const firstSub = getFirstSubscriptionForMobileSubTab(subs, activeMobileTab);
        if (!firstSub) {
            const fallback = subs.find((s) => s.dataUnlimited) ?? subs[0];
            return {
                offeringCode: fallback ? `${fallback.offeringCode}.${fallback.icim}` : undefined,
                resetMobileTab: "UNLIMITED",
            };
        }
        return { offeringCode: `${firstSub.offeringCode}.${firstSub.icim}` };
    }
    if (cat === "TJENESTER") {
        return { offeringCode: getFirstOfferingCode(icims.vas, preferredGroup) };
    }
    if (cat === "BROADBAND") {
        if (activeBroadbandTab === "FWA_TRADLOS") {
            return { offeringCode: undefined };
        }
        const offeringCode = getFirstOfferingCode(icims.mbb, preferredGroup);
        return {
            offeringCode,
            resetBroadbandTab: offeringCode ? undefined : "MBB_MOBIL",
        };
    }
    return { offeringCode: undefined };
};
